import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiClient } from "./api";

const LS = (id) => `cartal_draft_${id}`;
const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID()
  : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0; return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    }));

// Auto-saves the sell form as a server draft (+ local backup); restores from ?draft=<id>.
export function useDraftAutosave({ form, setForm, formRef }) {
  const [params, setParams] = useSearchParams();
  const [draftId, setDraftId] = useState(params.get("draft") || null);
  const [status, setStatus] = useState("idle"); // idle | saving | saved | offline
  const [savedAt, setSavedAt] = useState(null);
  const dirty = useRef(false);
  const restoring = useRef(!!params.get("draft"));
  const lastSection = useRef(null);
  const initialForm = useRef(form);
  const openedAt = useRef(Date.now());
  const skipNext = useRef(false);
  const pendingSince = useRef(0);
  const timer = useRef(null);
  const formLatest = useRef(form);
  formLatest.current = form;

  useEffect(() => {
    const id = params.get("draft");
    if (!id) return;
    const local = JSON.parse(localStorage.getItem(LS(id)) || "null");
    apiClient.get(`/drafts/${id}`).then(({ data }) => {
      const useLocal = local && local.at > Date.parse(data.updated_at || 0);
      skipNext.current = !useLocal;
      setForm((f) => ({ ...f, ...(useLocal ? local.form : data.form) }));
      lastSection.current = (useLocal ? local.section : data.last_section) || null;
      if (useLocal) dirty.current = true;
      setSavedAt(data.updated_at);
      setStatus("saved");
    }).catch(() => {
      if (local) { setForm((f) => ({ ...f, ...local.form })); dirty.current = true; }
    }).finally(() => {
      restoring.current = false;
      setTimeout(() => {
        const el = lastSection.current && document.querySelector(`[data-testid="${lastSection.current}"]`);
        if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 400);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onFocus = (e) => {
      if (!formRef.current || !formRef.current.contains(e.target)) return;
      const t = e.target.closest("[data-testid]");
      if (t) lastSection.current = t.getAttribute("data-testid");
    };
    document.addEventListener("focusin", onFocus);
    return () => document.removeEventListener("focusin", onFocus);
  }, [formRef]);

  const saveNow = useCallback(async () => {
    if (!dirty.current || restoring.current) return;
    let id = draftId;
    if (!id) {
      id = newId(); setDraftId(id);
      const p = new URLSearchParams(window.location.search); p.set("draft", id);
      setParams(p, { replace: true });
    }
    const snapshot = formLatest.current;
    localStorage.setItem(LS(id), JSON.stringify({ form: snapshot, section: lastSection.current, at: Date.now() }));
    setStatus("saving");
    pendingSince.current = 0;
    try {
      const { data } = await apiClient.put(`/drafts/${id}`, { form: snapshot, last_section: lastSection.current });
      dirty.current = false;
      setSavedAt(data.updated_at); setStatus("saved");
    } catch {
      setStatus("offline");
    }
  }, [draftId, setParams]);

  const saveRef = useRef(null);
  saveRef.current = saveNow;
  useEffect(() => {
    if (form === initialForm.current) return;
    // Ignore programmatic prefills right after the page opens.
    if (Date.now() - openedAt.current < 2000) { initialForm.current = form; return; }
    if (restoring.current || skipNext.current) { skipNext.current = false; return; }
    dirty.current = true;
    if (!pendingSince.current) pendingSince.current = Date.now();
    clearTimeout(timer.current);
    const wait = Date.now() - pendingSince.current > 30000 ? 0 : 3000;
    timer.current = setTimeout(() => saveRef.current(), wait);
  }, [form]);

  useEffect(() => {
    const onHide = () => { if (document.visibilityState === "hidden") saveNow(); };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", saveNow);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", saveNow);
      clearTimeout(timer.current);
    };
  }, [saveNow]);

  const clearLocal = useCallback(() => { if (draftId) localStorage.removeItem(LS(draftId)); }, [draftId]);

  return { draftId, status, savedAt, saveNow, clearLocal };
}
